import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Shield, Lock, Key, Eye, EyeOff, FileText } from 'lucide-react';
import { DisclaimerText } from './DisclaimerText';

export function Login() {
  const { state, createLedger, unlockLedger, storage } = useApp();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [hasExistingLedger, setHasExistingLedger] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  // Form states
  const [passphrase, setPassphrase] = useState('');
  const [householdName, setHouseholdName] = useState('');
  const [ownerName, setOwnerName] = useState('');

  // Check if ledger exists
  useEffect(() => {
    const checkLedger = () => {
      // Mock check - in real app this would check if household exists in storage
      const existing = storage.getHousehold();
      setHasExistingLedger(!!existing);
    };
    checkLedger();
  }, [storage]);

  // Redirect if already authenticated
  if (state.auth.isUnlocked) {
    return <Navigate to="/" replace />;
  }

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passphrase.trim()) return;

    setIsLoading(true);
    setError('');

    try {
      const success = await unlockLedger(passphrase);
      
      if (!success) {
        setError('Invalid passphrase. Please try again.');
      }
    } catch (err) {
      setError('Failed to unlock ledger. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passphrase.trim() || !householdName.trim() || !ownerName.trim()) {
      setError('All fields are required');
      return;
    }

    if (passphrase.length < 8) {
      setError('Passphrase must be at least 8 characters long');
      return;
    }

    // Show disclaimer before creating ledger
    setShowDisclaimer(true);
  };

  const handleDisclaimerAccept = async () => {
    if (!disclaimerAccepted) {
      setError('You must accept the terms to continue');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const success = await createLedger(passphrase, householdName, ownerName);
      
      if (!success) {
        setError('Failed to create ledger. Please try again.');
        setShowDisclaimer(false);
      }
    } catch (err) {
      setError('Failed to create ledger. Please try again.');
      setShowDisclaimer(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisclaimerCancel = () => {
    setShowDisclaimer(false);
    setDisclaimerAccepted(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center gradient-bg p-4 sm:p-8">
      <div className="w-full max-w-md">
        {/* Logo and Branding */}
        <div className="text-center mb-6 sm:mb-8 animate-fade-in">
          <div className="mx-auto p-3 sm:p-4 bg-primary/10 rounded-2xl mb-3 sm:mb-4 w-fit">
            <Shield className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-1 sm:mb-2">PrivyBudget</h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            The privacy-first budget app
          </p>
        </div>

        <Card className="shadow-soft animate-slide-up border-0 bg-card/95 backdrop-blur-sm">
          <CardHeader className="text-center space-y-2 pb-3 sm:pb-4 px-4 sm:px-6">
          </CardHeader>
        
        <CardContent className="px-4 sm:px-6 pb-4 sm:pb-6">
          {showDisclaimer ? (
            // Disclaimer Screen
            <div className="space-y-4 sm:space-y-6">
              <div className="text-center">
                <div className="mx-auto w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 dark:bg-orange-900/20 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                  <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600 dark:text-orange-400" />
                </div>
                <h2 className="text-lg sm:text-xl font-semibold mb-2">Disclaimer</h2>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Please read and accept our terms before proceeding
                </p>
              </div>

              <div className="max-h-48 sm:max-h-64 overflow-y-auto border rounded-lg p-3 sm:p-4 bg-muted/30 custom-scrollbar">
                <DisclaimerText />
              </div>

              <div className="flex items-start space-x-3 p-3 sm:p-4 bg-muted/20 rounded-lg">
                <Checkbox
                  id="disclaimer-accept"
                  checked={disclaimerAccepted}
                  onCheckedChange={(checked) => setDisclaimerAccepted(!!checked)}
                  data-testid="disclaimer-checkbox"
                  className="mt-0.5"
                />
                <Label 
                  htmlFor="disclaimer-accept" 
                  className="text-sm font-medium leading-5 cursor-pointer"
                >
                  I understand and accept these terms
                </Label>
              </div>

              {error && (
                <Alert className="border-destructive/50 text-destructive dark:border-destructive">
                  <AlertDescription className="text-sm">{error}</AlertDescription>
                </Alert>
              )}

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleDisclaimerCancel}
                  className="btn-mobile-outline w-full sm:flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  type="button" 
                  onClick={handleDisclaimerAccept}
                  disabled={!disclaimerAccepted || isLoading}
                  className="btn-mobile-primary w-full sm:flex-1"
                  data-testid="accept-disclaimer-btn"
                >
                  {isLoading ? 'Creating...' : 'Accept & Continue'}
                </Button>
              </div>
            </div>
          ) : (
            // Normal Login Flow
            <Tabs defaultValue="create" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="create" data-testid="create-tab">Create New</TabsTrigger>
              <TabsTrigger value="unlock" data-testid="unlock-tab">
                Unlock Existing
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="create" className="mt-6">
              <div className="text-center mb-6">
                <h2 className="text-lg font-semibold mb-2">Create Your Ledger</h2>
                <p className="text-sm text-muted-foreground">
                  Set up your secure, encrypted financial ledger
                </p>
              </div>
              
              <form onSubmit={handleCreate} className="space-y-4" data-testid="setup-form">
                <div className="space-y-2">
                  <Label htmlFor="household-name">Household Name</Label>
                  <Input
                    id="household-name"
                    type="text"
                    placeholder="e.g., The Smith Family"
                    value={householdName}
                    onChange={(e) => setHouseholdName(e.target.value)}
                    data-testid="household-name-input"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="owner-name">Your Name</Label>
                  <Input
                    id="owner-name"
                    type="text"
                    placeholder="e.g., John Smith"
                    value={ownerName}
                    onChange={(e) => setOwnerName(e.target.value)}
                    data-testid="owner-name-input"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="create-passphrase">Master Passphrase</Label>
                  <div className="relative">
                    <Input
                      id="create-passphrase"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter a strong passphrase (min 8 characters)"
                      value={passphrase}
                      onChange={(e) => setPassphrase(e.target.value)}
                      data-testid="create-passphrase-input"
                      required
                      minLength={8}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    This passphrase encrypts all your financial data. Choose something strong and memorable.
                  </p>
                </div>

                <Alert>
                  <Shield className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Important:</strong> Your passphrase cannot be recovered. Write it down and store it safely.
                    All data is encrypted locally and never sent to our servers.
                  </AlertDescription>
                </Alert>

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={isLoading}
                  data-testid="create-ledger-button"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin mr-2" />
                      Creating Ledger...
                    </>
                  ) : (
                    <>
                      <Key className="w-4 h-4 mr-2" />
                      Create Secure Ledger
                    </>
                  )}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="unlock" className="mt-6">
              <div className="text-center mb-6">
                <h2 className="text-lg font-semibold mb-2">Unlock Ledger</h2>
                <p className="text-sm text-muted-foreground">
                  Enter your passphrase to access your data
                </p>
              </div>
              
              <form onSubmit={handleUnlock} className="space-y-4" data-testid="unlock-form">
                <div className="space-y-2">
                  <Label htmlFor="unlock-passphrase">Master Passphrase</Label>
                  <div className="relative">
                    <Input
                      id="unlock-passphrase"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your passphrase"
                      value={passphrase}
                      onChange={(e) => setPassphrase(e.target.value)}
                      data-testid="unlock-passphrase-input"
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={isLoading}
                  data-testid="unlock-ledger-button"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin mr-2" />
                      Unlocking...
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4 mr-2" />
                      Unlock Ledger
                    </>
                  )}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
          )}

          {!showDisclaimer && error && (
            <Alert variant="destructive" className="mt-4" data-testid="error-alert">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="mt-6 pt-4 border-t border-border/50">
            <div className="flex items-center justify-center space-x-4 text-xs text-muted-foreground">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                <span>End-to-end encrypted</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                <span>Local-first</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
}